import { IMPORT_OUTCOME, IMPORT_SHIPMENT_KIND, type ImportOutcome, type ImportPreviewRow, type ImportPushBody, type ImportResult } from '@/types/poTracker';

import { FORWARDERS, latestShippedOn, type LineDraft } from './poTracker';

export interface RowEdit {
  skipped: boolean;
  date: string;
  forwarder: string;
  hawb: string;
  note: string;
  drafts: LineDraft[];
}

// The server's limit on forwarder and HAWB/BOL text, after trimming.
export const SHIPPING_TEXT_MAX = 60;
// The server's limit on a shipment note.
export const NOTE_MAX = 500;
const HAND_SPLIT = '+';
const WHITESPACE = /\s+/g;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const WHOLE_NUMBER = /^\d+$/;

// Rows the partner cannot push at all; they never reach the request.
const UNPUSHABLE: ImportOutcome[] = [IMPORT_OUTCOME.SKIPPED_UNKNOWN_PO, IMPORT_OUTCOME.SKIPPED_HAS_SHIPMENTS];

// A card the partner split off by hand: "518+1".
export const isHandSplit = (row: ImportPreviewRow): boolean => row.rowKey.includes(HAND_SPLIT);

// How messages name a row: the sheet row, plus the PO for one PO of a split cell, or the waybill of a split waybill cell.
export function rowLabel(row: ImportPreviewRow): string {
  if (row.splitFrom) return `${row.rowNumber} (${row.poNumber})`;
  if (row.splitWaybills || isHandSplit(row)) return `${row.rowNumber} (${row.hawb ?? 'new shipment'})`;
  return String(row.rowNumber);
}

// The preview carries no sheet total for a split cell, so the note names the cell it came from.
export function splitNote(row: ImportPreviewRow): string | null {
  const cell = row.splitWaybills ?? row.splitFrom;
  return cell ? `split from ${cell.replace(WHITESPACE, ' ').trim()}` : null;
}

// A sibling card on the same sheet row, same PO and candidates, for the partner to give lines and pieces.
export function splitRow(rows: ImportPreviewRow[], row: ImportPreviewRow): { rows: ImportPreviewRow[]; added: ImportPreviewRow } {
  const base = row.rowKey.split(HAND_SPLIT)[0] ?? row.rowKey;
  const isSibling = (r: ImportPreviewRow): boolean => r.rowKey === base || r.rowKey.startsWith(`${base}${HAND_SPLIT}`);
  const siblings = rows.filter(isSibling);
  const added: ImportPreviewRow = {
    ...row,
    rowKey: `${base}${HAND_SPLIT}${siblings.length}`,
    quantity: null,
    hawb: null,
    note: null,
    outcome: IMPORT_OUTCOME.NEEDS_LINES,
    reason: 'Pick the lines and pieces of this shipment',
    checkSplit: false,
    shipment: null,
  };
  const last = rows.lastIndexOf(siblings[siblings.length - 1] ?? row);
  return { rows: [...rows.slice(0, last + 1), added, ...rows.slice(last + 1)], added };
}

export const isShipped = (row: ImportPreviewRow): boolean => row.kind === IMPORT_SHIPMENT_KIND.SHIPPED;

// What the backend would date a row whose lines didn't resolve: ATD else ETD when shipped, ETD when planned.
const sheetDate = (row: ImportPreviewRow): string => (isShipped(row) ? [row.atd, row.etd] : [row.etd]).map((t) => t.trim()).find((t) => ISO_DATE.test(t)) ?? '';

export function initialEdit(row: ImportPreviewRow): RowEdit {
  return {
    skipped: false,
    date: row.shipment ? (row.shipment.date ?? '') : sheetDate(row),
    forwarder: row.forwarder ?? '',
    hawb: row.hawb ?? '',
    note: row.note ?? '',
    drafts: (row.shipment?.lines ?? []).map((l) => ({ odooLineId: l.odooLineId, lineNo: l.lineNo, qty: String(l.quantity) })),
  };
}

function editOf(edits: Map<string, RowEdit>, row: ImportPreviewRow): RowEdit {
  return edits.get(row.rowKey) ?? initialEdit(row);
}

// Rows whose lines the backend didn't settle open with the picker.
const PICK_LINES: ImportOutcome[] = [IMPORT_OUTCOME.NEEDS_LINES, IMPORT_OUTCOME.OVER_ORDER];

export function picksLines(row: ImportPreviewRow): boolean {
  if (PICK_LINES.includes(row.outcome)) return true;
  return row.candidates !== null && (row.shipment?.lines ?? []).length === 0;
}

const isFutureShipment = (row: ImportPreviewRow, date: string, today: string): boolean => isShipped(row) && date > latestShippedOn(today);

// Decided from the sheet, not the live edit, so the input stays while the partner types the date.
export function needsDateInput(row: ImportPreviewRow, today: string): boolean {
  const date = initialEdit(row).date;
  return row.outcome === IMPORT_OUTCOME.NEEDS_DATE || date === '' || isFutureShipment(row, date, today);
}

// A forwarder typed in the sheet that isn't on the list stays pickable.
export function forwarderOptions(sheetForwarder: string | null): string[] {
  const typed = sheetForwarder?.trim() ?? '';
  return typed && !(FORWARDERS as readonly string[]).includes(typed) ? [...FORWARDERS, typed] : [...FORWARDERS];
}

export const IMPORT_TAB = { READY: 'ready', NEEDS_INPUT: 'needs_input', SKIPPED: 'skipped' } as const;
export type ImportTab = (typeof IMPORT_TAB)[keyof typeof IMPORT_TAB];

// From the current edit, not the backend outcome, so a row moves to Ready as it's fixed.
export function rowTab(row: ImportPreviewRow, edit: RowEdit, problem: string | null): ImportTab {
  if (!isKept(row, edit)) return IMPORT_TAB.SKIPPED;
  return problem === null ? IMPORT_TAB.READY : IMPORT_TAB.NEEDS_INPUT;
}

// The one line of text a row's header carries: why it's skipped, what's missing, or the backend's note
// on a resolved row. A problem outcome's reason goes stale once the partner fixes the row, so it isn't shown.
export function rowNote(row: ImportPreviewRow, edit: RowEdit, problem: string | null): string | null {
  if (!isKept(row, edit)) return edit.skipped ? 'Skipped by you' : row.reason;
  if (problem !== null) return problem;
  return row.outcome === IMPORT_OUTCOME.RESOLVED ? row.reason : null;
}

export function isKept(row: ImportPreviewRow, edit: RowEdit): boolean {
  return !edit.skipped && !UNPUSHABLE.includes(row.outcome);
}

// Live, so the counts follow every edit.
export function countByTab(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, problems: Map<string, string | null>): Record<ImportTab, number> {
  const counts: Record<ImportTab, number> = { [IMPORT_TAB.READY]: 0, [IMPORT_TAB.NEEDS_INPUT]: 0, [IMPORT_TAB.SKIPPED]: 0 };
  rows.forEach((r) => {
    counts[rowTab(r, editOf(edits, r), problems.get(r.rowKey) ?? null)] += 1;
  });
  return counts;
}

// Which tab lists each row. Retaken only on preview, tab change and Push, so a row being fixed doesn't
// leave the list (and drop focus) at the first keystroke.
export function tabSnapshot(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, problems: Map<string, string | null>): Map<string, ImportTab> {
  return new Map(rows.map((r) => [r.rowKey, rowTab(r, editOf(edits, r), problems.get(r.rowKey) ?? null)]));
}

const sendsShipping = (row: ImportPreviewRow, edit: RowEdit): boolean => isKept(row, edit) && isShipped(row);

// Rows sharing a HAWB/BOL become one shipping event, so the server rejects the whole push when they disagree.
// Imported rows count too: the server reuses their shipping event and fails a PO that disagrees with it,
// and they are read-only, so the message names them as the one to match.
export function hawbConflicts(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, results: Map<string, RowImportResult> = new Map()): Map<string, string> {
  const shipped = rows.map((r) => ({ row: r, edit: editOf(edits, r) })).filter(({ row: r, edit }) => sendsShipping(r, edit) && edit.hawb.trim() !== '');
  const conflicts = new Map<string, string>();
  shipped.forEach(({ row: r, edit }) => {
    const hawb = edit.hawb.trim();
    const clashes = shipped.filter(
      (o) => o.row.rowKey !== r.rowKey && o.edit.hawb.trim() === hawb && (o.edit.date !== edit.date || o.edit.forwarder.trim() !== edit.forwarder.trim())
    );
    const imported = clashes.find((o) => isRowImported(results, o.row.rowKey));
    if (imported) conflicts.set(r.rowKey, `HAWB/BOL ${hawb} is already imported on row ${rowLabel(imported.row)} with a different date or forwarder: match it`);
    else if (clashes[0]) conflicts.set(r.rowKey, `HAWB/BOL ${hawb} is also on row ${rowLabel(clashes[0].row)} with a different date or forwarder`);
  });
  return conflicts;
}

function knownLines(row: ImportPreviewRow): Set<number> {
  return new Set([...(row.candidates ?? []).map((c) => c.odooLineId), ...(row.shipment?.lines ?? []).map((l) => l.odooLineId)]);
}

// Same rule as Plan a shipment: whole pieces, at least 1.
const isWholePositive = (qty: string): boolean => WHOLE_NUMBER.test(qty.trim()) && Number(qty) >= 1;

function shippingProblem(row: ImportPreviewRow, edit: RowEdit, today: string): string | null {
  if (!isShipped(row)) return null;
  if (isFutureShipment(row, edit.date, today)) return "Shipped date can't be in the future";
  if (edit.forwarder.trim() === '') return 'Pick a forwarder';
  if (edit.forwarder.trim().length > SHIPPING_TEXT_MAX) return `Forwarder is longer than ${SHIPPING_TEXT_MAX} characters`;
  if (edit.hawb.trim().length > SHIPPING_TEXT_MAX) return `HAWB/BOL is longer than ${SHIPPING_TEXT_MAX} characters`;
  return null;
}

const noteProblem = (edit: RowEdit): string | null => (edit.note.trim().length > NOTE_MAX ? `Note is longer than ${NOTE_MAX} characters` : null);

function overFree(lineNo: number, left: number): string {
  // Negative when the other rows of the PO already take more than the line has free.
  return left < 0 ? `Line ${lineNo}: other rows of this PO take more than is left` : `Line ${lineNo}: only ${left} pcs left`;
}

// today is the viewer's ISO day; hawbConflict comes from hawbConflicts().
export function rowProblem(row: ImportPreviewRow, edit: RowEdit, free: Map<number, number>, today: string, hawbConflict: string | null): string | null {
  if (edit.date === '') return 'Pick a date';
  const shipping = shippingProblem(row, edit, today) ?? noteProblem(edit);
  if (shipping) return shipping;
  if (hawbConflict) return hawbConflict;
  if (!edit.drafts.some((d) => isWholePositive(d.qty))) return 'Pick the lines';
  const known = knownLines(row);
  for (const d of edit.drafts) {
    if (!isWholePositive(d.qty)) return `Line ${d.lineNo}: whole pieces only`;
    const left = free.get(d.odooLineId) ?? 0;
    if (Number(d.qty) > left) return known.has(d.odooLineId) ? overFree(d.lineNo, left) : `Line ${d.lineNo} is not on the PO`;
  }
  return null;
}

// Candidates carry the line's tracker room and received pieces before any row of the import took anything.
// Every OTHER kept row of the PO (earlier and later) takes from the room; the row's own drafts never do.
// A planned row also can't plan what is already received, less what the shipped rows already account for;
// received pieces are no cap for a shipped row (the backend's rule).
export function freePieces(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, poNumber: string, rowKey: string): Map<number, number> {
  const free = new Map<number, number>();
  const self = rows.find((r) => r.rowKey === rowKey);
  // A row without candidates has only its own lines, not a pool the others share.
  if (!self?.candidates) {
    (self?.shipment?.lines ?? []).forEach((l) => free.set(l.odooLineId, l.quantity));
    return free;
  }
  const others = rows.filter((r) => r.poNumber === poNumber && r.rowKey !== rowKey && isKept(r, editOf(edits, r)));
  const picked = (odooLineId: number, onlyShipped: boolean): number =>
    others
      .filter((r) => !onlyShipped || isShipped(r))
      .flatMap((r) => editOf(edits, r).drafts)
      .filter((d) => d.odooLineId === odooLineId && isWholePositive(d.qty))
      .reduce((sum, d) => sum + Number(d.qty), 0);
  self.candidates.forEach((c) => {
    const unshippedReceived = isShipped(self) ? 0 : Math.max(0, c.received - picked(c.odooLineId, true));
    free.set(c.odooLineId, c.free - picked(c.odooLineId, false) - unshippedReceived);
  });
  return free;
}

export function unresolved(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, today: string, results: Map<string, RowImportResult> = new Map()): string[] {
  const conflicts = hawbConflicts(rows, edits, results);
  return rows
    .filter((r) => {
      const edit = editOf(edits, r);
      return isKept(r, edit) && rowProblem(r, edit, freePieces(rows, edits, r.poNumber, r.rowKey), today, conflicts.get(r.rowKey) ?? null) !== null;
    })
    .map((r) => r.rowKey);
}

export function inputNeededText(n: number): string {
  return n === 1 ? '1 row needs your input' : `${n} rows need your input`;
}

export function buildPushRequest(vendorId: string, rows: ImportPreviewRow[], edits: Map<string, RowEdit>): ImportPushBody {
  const shipments = rows.flatMap((row) => {
    const edit = editOf(edits, row);
    if (!isKept(row, edit)) return [];
    const shipped = isShipped(row);
    return [
      {
        rowNumber: row.rowNumber,
        rowKey: row.rowKey,
        poNumber: row.poNumber,
        kind: shipped ? IMPORT_SHIPMENT_KIND.SHIPPED : IMPORT_SHIPMENT_KIND.PLANNED,
        date: edit.date,
        // The server ignores a planned row's forwarder and HAWB but still validates them.
        forwarder: shipped ? edit.forwarder.trim() || null : null,
        hawb: shipped ? edit.hawb.trim() || null : null,
        note: edit.note.trim() || null,
        lines: edit.drafts.map((d) => ({ odooLineId: d.odooLineId, quantity: Number(d.qty) })),
      },
    ];
  });
  return { vendorId, shipments };
}

export const nothingToImport = (request: ImportPushBody): boolean => request.shipments.length === 0;

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export function pushSummary(result: ImportResult): string {
  const shipments = result.written.reduce((sum, w) => sum + w.shipments, 0);
  const done = `${plural(shipments, 'shipment', 'shipments')} on ${plural(result.written.length, 'PO', 'POs')} imported`;
  if (result.failed.length === 0) return done;
  return `${done}, ${result.failed.length} ${result.failed.length === 1 ? 'PO failed' : 'POs failed'}`;
}

// Row at a time: what the last push did to each row, by rowKey. A row not in the map hasn't been pushed yet.
export const ROW_IMPORT_STATE = { IMPORTED: 'imported', FAILED: 'failed' } as const;
export type RowImportResult = { state: typeof ROW_IMPORT_STATE.IMPORTED } | { state: typeof ROW_IMPORT_STATE.FAILED; reason: string };

// The server answers per PO: every row the request carried for a written PO is imported, for a failed PO failed.
export function applyImportResult(results: Map<string, RowImportResult>, rows: ImportPreviewRow[], request: ImportPushBody, result: ImportResult): Map<string, RowImportResult> {
  const next = new Map(results);
  const written = new Set(result.written.map((w) => w.poNumber));
  const failed = new Map(result.failed.map((f) => [f.poNumber, f.reason]));
  request.shipments.forEach((s) => {
    const row = rows.find((r) => r.rowKey === s.rowKey);
    if (!row) return;
    if (written.has(s.poNumber)) next.set(row.rowKey, { state: ROW_IMPORT_STATE.IMPORTED });
    const reason = failed.get(s.poNumber);
    if (reason !== undefined) next.set(row.rowKey, { state: ROW_IMPORT_STATE.FAILED, reason });
  });
  return next;
}

export const isRowImported = (results: Map<string, RowImportResult>, rowKey: string): boolean => results.get(rowKey)?.state === ROW_IMPORT_STATE.IMPORTED;

export function failedReason(results: Map<string, RowImportResult>, rowKey: string): string | null {
  const result = results.get(rowKey);
  return result?.state === ROW_IMPORT_STATE.FAILED ? result.reason : null;
}

// Every PO with at least one imported row: what Done refreshes.
export function importedPoNumbers(rows: ImportPreviewRow[], results: Map<string, RowImportResult>): string[] {
  return poOrder(rows.filter((r) => isRowImported(results, r.rowKey)));
}

// Rows still to import; an imported row is read-only and never pushed again.
export function openRows(rows: ImportPreviewRow[], results: Map<string, RowImportResult>): ImportPreviewRow[] {
  return rows.filter((r) => !isRowImported(results, r.rowKey));
}

// Over every row, so an imported sibling still takes its pieces (they are on the tracker now, which the
// preview's free counts don't know); only the imported rows themselves drop out.
export function pendingRows(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, today: string, results: Map<string, RowImportResult>): string[] {
  return unresolved(rows, edits, today, results).filter((key) => !isRowImported(results, key));
}

// Which rows each Import button sends: kept rows not yet imported, of that PO, that row, or every ready PO.
export function pushRequestForPo(vendorId: string, rows: ImportPreviewRow[], edits: Map<string, RowEdit>, results: Map<string, RowImportResult>, poNumber: string): ImportPushBody {
  return buildPushRequest(
    vendorId,
    openRows(rows, results).filter((r) => r.poNumber === poNumber),
    edits
  );
}

export function pushRequestForRow(vendorId: string, rows: ImportPreviewRow[], edits: Map<string, RowEdit>, results: Map<string, RowImportResult>, rowKey: string): ImportPushBody {
  return buildPushRequest(
    vendorId,
    openRows(rows, results).filter((r) => r.rowKey === rowKey),
    edits
  );
}

// POs with a kept row still to import and no row needing input; pending comes from pendingRows().
export function readyPoNumbers(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, pending: string[], results: Map<string, RowImportResult>): string[] {
  const blocked = new Set(rows.filter((r) => pending.includes(r.rowKey)).map((r) => r.poNumber));
  return poOrder(openRows(rows, results).filter((r) => isKept(r, editOf(edits, r)) && !blocked.has(r.poNumber)));
}

export function pushRequestForReady(
  vendorId: string,
  rows: ImportPreviewRow[],
  edits: Map<string, RowEdit>,
  pending: string[],
  results: Map<string, RowImportResult>
): ImportPushBody {
  const ready = new Set(readyPoNumbers(rows, edits, pending, results));
  return buildPushRequest(
    vendorId,
    openRows(rows, results).filter((r) => ready.has(r.poNumber)),
    edits
  );
}

// pending comes from pendingRows() over every row, so a HAWB/BOL clash with another PO still counts.
export function pendingForPo(rows: ImportPreviewRow[], pending: string[], poNumber: string): string[] {
  const own = new Set(rows.filter((r) => r.poNumber === poNumber).map((r) => r.rowKey));
  return pending.filter((key) => own.has(key));
}

const poOrder = (rows: ImportPreviewRow[]): string[] => [...new Set(rows.map((r) => r.poNumber))];

// A PO is listed under a tab when at least one of its rows is, by the snapshot; in the order the sheet first names it.
export function posInTab(rows: ImportPreviewRow[], snapshot: Map<string, ImportTab>, tab: ImportTab): string[] {
  return poOrder(rows).filter((poNumber) => rows.some((r) => r.poNumber === poNumber && snapshot.get(r.rowKey) === tab));
}

export interface PoSummary {
  poNumber: string;
  rows: number;
  // Over the rows not yet imported.
  counts: Record<ImportTab, number>;
  imported: number;
  // Every kept row is on the tracker: nothing ready or needing input is left.
  done: boolean;
  failed: string | null;
  text: string;
}

const TAB_WORDS: [ImportTab, string][] = [
  [IMPORT_TAB.READY, 'ready'],
  [IMPORT_TAB.NEEDS_INPUT, 'needs input'],
  [IMPORT_TAB.SKIPPED, 'skipped'],
];

function summaryText(counts: Record<ImportTab, number>, imported: number, done: boolean, failed: string | null): string {
  if (failed !== null) return `Failed: ${failed}`;
  if (done) return `Imported · ${plural(imported, 'shipment', 'shipments')}`;
  const parts = TAB_WORDS.filter(([tab]) => counts[tab] > 0).map(([tab, word]) => `${counts[tab]} ${word}`);
  return [...(imported > 0 ? [`${imported} imported`] : []), ...parts].join(' · ');
}

// The left list, in sheet order. Counts are live (problems as computed now), like the tab counts.
export function poSummaries(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, problems: Map<string, string | null>, results: Map<string, RowImportResult>): PoSummary[] {
  return poOrder(rows).map((poNumber) => {
    const own = rows.filter((r) => r.poNumber === poNumber);
    const open = openRows(own, results);
    const counts = countByTab(open, edits, problems);
    const imported = own.length - open.length;
    const done = imported > 0 && counts[IMPORT_TAB.READY] + counts[IMPORT_TAB.NEEDS_INPUT] === 0;
    const failed = open.map((r) => failedReason(results, r.rowKey)).find((reason) => reason !== null) ?? null;
    return { poNumber, rows: own.length, counts, imported, done, failed, text: summaryText(counts, imported, done, failed) };
  });
}

// POs with an imported row and a kept row still to import: a later preview skips a PO already on the
// tracker, so those rows can only be added by hand once the dialog closes.
export function partlyImportedPos(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, results: Map<string, RowImportResult>): { poNumber: string; left: number }[] {
  return poOrder(rows).flatMap((poNumber) => {
    const own = rows.filter((r) => r.poNumber === poNumber);
    if (!own.some((r) => isRowImported(results, r.rowKey))) return [];
    const left = openRows(own, results).filter((r) => isKept(r, editOf(edits, r))).length;
    return left > 0 ? [{ poNumber, left }] : [];
  });
}

export function closeWarningText(partly: { poNumber: string; left: number }[]): string | null {
  if (partly.length === 0) return null;
  const left = partly.reduce((sum, p) => sum + p.left, 0);
  const pos = partly.map((p) => p.poNumber).join(', ');
  return `${left === 1 ? '1 row' : `${left} rows`} of ${pos} ${left === 1 ? 'is' : 'are'} not imported yet. Once you close, they can only be added by hand.`;
}

// What an imported row carried, for its read-only view.
export function draftLines(row: ImportPreviewRow, edit: RowEdit): { lineNo: number; partNumber: string | null; quantity: number }[] {
  const parts = new Map<number, string | null>([
    ...(row.shipment?.lines ?? []).map((l): [number, string | null] => [l.odooLineId, l.partNumber]),
    ...(row.candidates ?? []).map((c): [number, string | null] => [c.odooLineId, c.partNumber]),
  ]);
  return edit.drafts.map((d) => ({ lineNo: d.lineNo, partNumber: parts.get(d.odooLineId) ?? null, quantity: Number(d.qty) }));
}

export const hasPiecesLeft = (free: Map<number, number>): boolean => [...free.values()].some((n) => n > 0);

// The other kept rows of the PO taking this row's candidate lines, earlier or later in the sheet, as row labels.
export function blockingRows(rows: ImportPreviewRow[], edits: Map<string, RowEdit>, poNumber: string, rowKey: string): string[] {
  const self = rows.find((r) => r.rowKey === rowKey);
  const lines = new Set((self?.candidates ?? []).map((c) => c.odooLineId));
  return rows
    .filter((r) => r.poNumber === poNumber && r.rowKey !== rowKey)
    .filter((r) => {
      const edit = editOf(edits, r);
      return isKept(r, edit) && edit.drafts.some((d) => lines.has(d.odooLineId) && isWholePositive(d.qty));
    })
    .map(rowLabel);
}

const listRows = (ns: string[]): string => (ns.length === 1 ? `row ${ns[0]}` : `rows ${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`);

// Shown instead of the line picker when nothing is left to pick.
export function noPiecesText(blocking: string[]): string {
  if (blocking.length === 0) return 'No pieces left for this row on this PO. Skip this row.';
  const verb = blocking.length === 1 ? 'takes' : 'take';
  return `No pieces left for this row: ${listRows(blocking)} of this PO already ${verb} these lines. Skip this row, or change ${blocking.length === 1 ? 'it' : 'theirs'}.`;
}
